import { FONT_DISPLAY } from './textures';

/** The paper's ink. */
const INK = [59, 42, 30];

function picture(src: string): Promise<HTMLImageElement | null> {
  return new Promise((done) => {
    if (!src) return done(null);
    const img = new Image();
    img.onload = () => done(img);
    img.onerror = () => done(null);
    img.src = src;
  });
}

/**
 * Yesterday's podium as a newspaper photo: the poster's little figures (winner first, then second and third)
 * on their steps before a crowd, the laurels behind the winner, printed through a 45° halftone screen in the
 * paper's brown ink on transparent (the paper shows through). A data URL, or '' where it can't be drawn.
 */
export async function pressPhoto(figures: string[], w = 640, h = 400): Promise<string> {
  const pics = await Promise.all(figures.map(picture));
  const tone = document.createElement('canvas');
  tone.width = w;
  tone.height = h;
  const g = tone.getContext('2d', { willReadFrequently: true });
  if (!g) return '';
  // The grey picture: a pale sky, a grandstand of heads, the steps, the drivers.
  const sky = g.createLinearGradient(0, 0, 0, h);
  sky.addColorStop(0, '#eeeeee');
  sky.addColorStop(1, '#dcdcdc');
  g.fillStyle = sky;
  g.fillRect(0, 0, w, h);
  for (let x = 0; x < w; x += 10) {
    for (let r = 0; r < 5; r++) {
      const v = 165 + ((x * 7 + r * 13) % 50);
      g.fillStyle = `rgb(${v},${v},${v})`;
      g.beginPath();
      g.ellipse(x + (r % 2) * 5 + 3.5, h * 0.3 + r * 12 + 3.5, 3.5, 3.5, 0, 0, Math.PI * 2);
      g.fill();
    }
  }
  g.fillStyle = 'rgb(135,135,135)';
  g.fillRect(0, h * 0.43, w, 6);
  const steps = [
    { x: w * 0.5, top: h * 0.68, i: 0, label: '1', fig: h * 0.6 },
    { x: w * 0.255, top: h * 0.75, i: 1, label: '2', fig: h * 0.54 },
    { x: w * 0.745, top: h * 0.795, i: 2, label: '3', fig: h * 0.51 },
  ];
  const bw = w * 0.265;
  // Laurels behind the winner: a ring of leaves, open at the top.
  g.fillStyle = 'rgb(105,105,105)';
  const cx = w * 0.503;
  const cy = h * 0.37;
  const R = h * 0.19;
  for (let k = 0; k < 9; k++) {
    for (const side of [-1, 1]) {
      const a = Math.PI / 2 + side * (0.35 + k * 0.29);
      g.save();
      g.translate(cx + Math.cos(a) * R, cy + Math.sin(a) * R);
      g.rotate(a + Math.PI / 2);
      g.beginPath();
      g.ellipse(0, 0, R * 0.2, R * 0.08, 0, 0, Math.PI * 2);
      g.fill();
      g.restore();
    }
  }
  for (const s of steps) {
    g.fillStyle = 'rgb(200,200,200)';
    g.fillRect(s.x - bw / 2, s.top, bw, h - s.top);
    g.fillStyle = 'rgb(172,172,172)';
    g.fillRect(s.x - bw / 2, s.top, bw, 7);
    const pic = pics[s.i];
    if (pic) {
      const fw = (pic.width * s.fig) / pic.height;
      g.save();
      g.filter = 'grayscale(1)';
      // Second place faces the winner, as on the poster.
      if (s.i === 1) {
        g.translate(s.x, 0);
        g.scale(-1, 1);
        g.drawImage(pic, -fw / 2, s.top - s.fig + 10, fw, s.fig);
      } else g.drawImage(pic, s.x - fw / 2, s.top - s.fig + 10, fw, s.fig);
      g.restore();
    }
    g.fillStyle = 'rgb(70,70,70)';
    g.font = `400 ${Math.round(h * 0.13)}px ${FONT_DISPLAY}`;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText(s.label, s.x, s.top + h * 0.11);
  }
  // The halftone: dots on a 45° screen, each as big as the picture is dark there.
  const px = g.getImageData(0, 0, w, h).data;
  const lum = (x: number, y: number): number => {
    const i = (Math.min(h - 1, Math.max(0, Math.round(y))) * w + Math.min(w - 1, Math.max(0, Math.round(x)))) * 4;
    return (px[i] * 0.3 + px[i + 1] * 0.59 + px[i + 2] * 0.11) / 255;
  };
  let lo = 1;
  let hi = 0;
  for (let i = 0; i < px.length; i += 16) {
    const v = (px[i] * 0.3 + px[i + 1] * 0.59 + px[i + 2] * 0.11) / 255;
    lo = Math.min(lo, v);
    hi = Math.max(hi, v);
  }
  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const o = out.getContext('2d');
  if (!o) return '';
  o.fillStyle = `rgba(${INK.join(',')},0.94)`;
  const cell = 4.6;
  const ca = Math.SQRT1_2;
  const n = Math.ceil(Math.hypot(w, h) / cell) + 2;
  for (let i = -n; i < n; i++) {
    for (let j = -n; j < n; j++) {
      const x = w / 2 + (i * ca - j * ca) * cell;
      const y = h / 2 + (i * ca + j * ca) * cell;
      if (x < -cell || y < -cell || x > w + cell || y > h + cell) continue;
      const v = (lum(x, y) - lo) / Math.max(0.01, hi - lo);
      const r = cell * 0.68 * Math.sqrt(Math.max(0, 1 - v));
      if (r < 0.3) continue;
      o.beginPath();
      o.arc(x, y, r, 0, Math.PI * 2);
      o.fill();
    }
  }
  return out.toDataURL('image/png');
}
